/**
 * DrugOutputField - Output component for drug array field
 *
 * Displays drug output data with collapsible sections for coverage, PA, etc.
 * Each section is a flat, top-level accordion item matching the schema structure.
 *
 * Exports:
 * - DrugOutputField: Main output component
 * - DrugOutputFieldPreview: Preview component for table cells
 */

import { Accordion } from '@components/ui/accordion';
import { SectionScoreBadge } from '@components/ui/disposition-status';
import { ChevronDown } from 'lucide-react';
import React, { useState } from 'react';

import { computeFieldScore } from '@/utils/dispositionCompleteness';
import { getCallSourceId } from '@/utils/evidence';

import { ItemDispositionProvider, ReviewProvider, SchemaProvider } from './context';
import { PillPreviewList, type PreviewPill } from './PillPreviewList';
import {
  AppealSection,
  CoverageSection,
  DenialSection,
  PBMSection,
  PreDeterminationProcessSection,
  PreDeterminationStatusSection,
  PriorAuthProcessSection,
  PriorAuthStatusSection,
  SpecialtyPharmacyCoverageSection,
  SpecialtyPharmacyDetailsSection,
  SpecialtyPharmacyPreDeterminationProcessSection,
  SpecialtyPharmacyPreDeterminationStatusSection,
  SpecialtyPharmacyPriorAuthProcessSection,
  SpecialtyPharmacyPriorAuthStatusSection,
  SpecialtyPharmacyStepTherapySection,
  StepTherapySection,
} from './sections';
import type { OutputArrayProps, PreviewProps } from './types';
import { type CloneableItem, CloneControls, useCloneItems } from './useCloneItems';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Drug output item shape (defined locally, schema provides field definitions at runtime)
 */
interface DrugItem {
  code?: string;
  name?: string;
  coverage?: Record<string, unknown>;
  prior_auth_status?: Record<string, unknown>;
  prior_auth_process?: Record<string, unknown>;
  pre_determination_status?: Record<string, unknown>;
  pre_determination_process?: Record<string, unknown>;
  denial?: Record<string, unknown>;
  appeal?: Record<string, unknown>;
  pbm?: Record<string, unknown>;
  specialty_pharmacy_coverage?: Record<string, unknown>;
  specialty_pharmacy_details?: Record<string, unknown>;
  specialty_pharmacy_pre_determination_process?: Record<string, unknown>;
  specialty_pharmacy_pre_determination_status?: Record<string, unknown>;
  specialty_pharmacy_prior_auth_process?: Record<string, unknown>;
  specialty_pharmacy_prior_auth_status?: Record<string, unknown>;
  specialty_pharmacy_step_therapy?: Record<string, unknown>;
  step_therapy?: Record<string, unknown>;
}

// =============================================================================
// MAIN OUTPUT COMPONENT
// =============================================================================

export const DrugOutputField: React.FC<OutputArrayProps> = ({
  data,
  outputMetadata,
  extractedMetadata,
  extractedData,
  onNavigateToCall,
  fieldId = 'drug',
  isHumanAudit,
  onAcceptExtracted,
  dispositionFields,
  currentCallId,
  onChange,
}) => {
  const [expandedDrugs, setExpandedDrugs] = useState<string[]>([]);

  // Cast data to expected shape (schema provides field definitions at runtime)
  const items = (Array.isArray(data) ? data : []) as DrugItem[];

  // Clone-mode controller (see useCloneItems). Enabled only when a write path
  // (onChange) is wired; needs at least two items to have somewhere to clone to.
  const clone = useCloneItems(items as CloneableItem[], onChange);
  const cloneEnabled = Boolean(onChange) && items.length > 1;

  if (items.length === 0) {
    return <div className="text-sm text-muted-foreground italic py-2">No drug data available</div>;
  }

  const toggleDrug = (drugId: string) => {
    setExpandedDrugs(prev =>
      prev.includes(drugId) ? prev.filter(id => id !== drugId) : [...prev, drugId],
    );
  };

  // Array-level metadata entries (may have source_id/source_call_id injected by page)
  const outputFieldMeta = outputMetadata?.fields?.[fieldId] as any;
  const extractedFieldMeta = extractedMetadata?.fields?.[fieldId] as any;
  const outputSourceCallId = getCallSourceId(outputFieldMeta);
  const extractedSourceCallId = getCallSourceId(extractedFieldMeta);
  const extractedDataArray = Array.isArray(extractedData) ? extractedData : [];

  return (
    <SchemaProvider>
      <div className="space-y-4">
        {items.map((drug, index) => {
          const drugId = `drug-${index}`;
          const isExpanded = expandedDrugs.includes(drugId);
          const drugLabel = drug.code || drug.name || `Drug ${index + 1}`;
          // Whole-item captured/required rollup (drug[N].* across all subsections),
          // mirroring the per-subsection badges inside.
          const itemScore = computeFieldScore(
            `${fieldId}[${index}]`,
            dispositionFields?.required_fields,
            dispositionFields?.missing_fields,
          );

          return (
            <ItemDispositionProvider
              key={drugId}
              arrayName={fieldId}
              index={index}
              dispositionFields={dispositionFields}
            >
              <ReviewProvider
                outputItemMeta={outputFieldMeta?.[index]}
                outputSourceCallId={outputSourceCallId}
                extractedItemMeta={extractedFieldMeta?.[index]}
                extractedSourceCallId={extractedSourceCallId}
                extractedItemData={extractedDataArray[index]}
                onNavigateToCall={onNavigateToCall}
                isHumanAudit={isHumanAudit}
                onAcceptExtracted={onAcceptExtracted}
                currentCallId={currentCallId}
              >
                <div className="border rounded-md bg-muted/30">
                  {/* Drug Header */}
                  <div className="flex items-center gap-2 pr-2">
                    <button
                      type="button"
                      onClick={() => toggleDrug(drugId)}
                      className="flex-1 flex items-center justify-between px-3 py-2 hover:bg-muted/50 transition-colors text-left rounded-md"
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-medium">{drugLabel}</span>
                        <SectionScoreBadge score={itemScore} />
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-muted-foreground transition-transform ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    <CloneControls
                      index={index}
                      clone={cloneEnabled ? clone : null}
                      entityLabel="drug"
                    />
                  </div>

                  {/* Drug Content - Flat Accordion Sections */}
                  {isExpanded && (
                    <Accordion
                      type="multiple"
                      defaultValue={[`${drugId}-coverage`, `${drugId}-prior-auth-status`]}
                      className="border-t"
                    >
                      {/* Core Drug Sections */}
                      <CoverageSection data={drug.coverage} sectionId={`${drugId}-coverage`} />
                      <PriorAuthStatusSection
                        data={drug.prior_auth_status}
                        sectionId={`${drugId}-prior-auth-status`}
                      />
                      <PriorAuthProcessSection
                        data={drug.prior_auth_process}
                        sectionId={`${drugId}-prior-auth-process`}
                      />
                      <PreDeterminationStatusSection
                        data={drug.pre_determination_status}
                        sectionId={`${drugId}-pre-determination-status`}
                      />
                      <PreDeterminationProcessSection
                        data={drug.pre_determination_process}
                        sectionId={`${drugId}-pre-determination-process`}
                      />
                      <AppealSection data={drug.appeal} sectionId={`${drugId}-appeal`} />
                      <DenialSection data={drug.denial} sectionId={`${drugId}-denial`} />
                      <StepTherapySection
                        data={drug.step_therapy}
                        sectionId={`${drugId}-step-therapy`}
                      />

                      {/* Specialty Pharmacy Sections */}
                      <SpecialtyPharmacyDetailsSection
                        data={drug.specialty_pharmacy_details}
                        sectionId={`${drugId}-sp-details`}
                      />
                      <SpecialtyPharmacyCoverageSection
                        data={drug.specialty_pharmacy_coverage}
                        sectionId={`${drugId}-sp-coverage`}
                      />
                      <SpecialtyPharmacyPriorAuthStatusSection
                        data={drug.specialty_pharmacy_prior_auth_status}
                        sectionId={`${drugId}-sp-pa-status`}
                      />
                      <SpecialtyPharmacyPriorAuthProcessSection
                        data={drug.specialty_pharmacy_prior_auth_process}
                        sectionId={`${drugId}-sp-pa-process`}
                      />
                      <SpecialtyPharmacyPreDeterminationStatusSection
                        data={drug.specialty_pharmacy_pre_determination_status}
                        sectionId={`${drugId}-sp-predet-status`}
                      />
                      <SpecialtyPharmacyPreDeterminationProcessSection
                        data={drug.specialty_pharmacy_pre_determination_process}
                        sectionId={`${drugId}-sp-predet-process`}
                      />
                      <SpecialtyPharmacyStepTherapySection
                        data={drug.specialty_pharmacy_step_therapy}
                        sectionId={`${drugId}-sp-step-therapy`}
                      />

                      {/* PBM Section */}
                      <PBMSection data={drug.pbm} sectionId={`${drugId}-pbm`} />
                    </Accordion>
                  )}
                </div>
              </ReviewProvider>
            </ItemDispositionProvider>
          );
        })}
      </div>
    </SchemaProvider>
  );
};

// =============================================================================
// PREVIEW COMPONENT
// =============================================================================

/** Item shape for preview (defined locally, schema provides field definitions at runtime) */
interface DrugPreviewItem {
  code?: string;
  name?: string;
}

/**
 * Generate a stable key from item properties.
 * Prefers code, then name, then falls back to index as last resort.
 */
function getStableKey(item: DrugPreviewItem, index: number): string {
  if (item.code) return `code-${item.code}`;
  if (item.name) return `name-${item.name}`;
  return `item-${index}`;
}

/**
 * DrugOutputFieldPreview - Pill-based preview component for drug output array field
 *
 * Displays drugs as pills showing code and optionally name. Overflow past `maxItems`
 * is reachable on hover — see {@link PillPreviewList}.
 */
export const DrugOutputFieldPreview: React.FC<PreviewProps> = ({ data, maxItems = 2 }) => {
  const items = (Array.isArray(data) ? data : []) as DrugPreviewItem[];

  const pills: PreviewPill[] = items.flatMap((drug, index) => {
    const parts = [drug.code, drug.name].filter(Boolean);
    if (parts.length === 0) return [];
    return [{ key: getStableKey(drug, index), text: parts.join(' · '), className: 'font-mono' }];
  });

  return <PillPreviewList pills={pills} maxItems={maxItems} />;
};
